import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useChatContext } from './ChatContext';

const LiveUpdatesContext = createContext(null);

// Reuses the chat socket connection (every internal user is connected to /chat
// by default). When chat is unavailable (e.g. portal users), socketRef is null
// and these hooks become no-ops.
export function LiveUpdatesProvider({ children }) {
  const { socketRef } = useChatContext();
  return (
    <LiveUpdatesContext.Provider value={{ socketRef }}>
      {children}
    </LiveUpdatesContext.Provider>
  );
}

function useLiveSocket() {
  const ctx = useContext(LiveUpdatesContext);
  return ctx?.socketRef || null;
}

// Subscribe to one or more socket events and invalidate the given React Query
// keys whenever any of them fire.
//   useInvalidateOnEvent('hr:payment:changed', [['payroll-entries'], ['payments-all']])
//   useInvalidateOnEvent(['hr:payment:changed','hr:payroll:changed'], [['hr-overview']])
export function useInvalidateOnEvent(events, queryKeys) {
  const socketRef = useLiveSocket();
  const qc = useQueryClient();
  const keysRef = useRef(queryKeys);
  keysRef.current = queryKeys;
  const socket = socketRef?.current;

  useEffect(() => {
    if (!socket) return;
    const list = Array.isArray(events) ? events : [events];
    const handler = () => {
      for (const key of keysRef.current || []) {
        qc.invalidateQueries({ queryKey: key });
      }
    };
    for (const e of list) socket.on(e, handler);
    return () => { for (const e of list) socket.off(e, handler); };
  }, [socket, events, qc]);
}

// Subscribe to a single event with a custom handler (for non-cache side effects).
export function useLiveEvent(event, handler) {
  const socketRef = useLiveSocket();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const socket = socketRef?.current;

  useEffect(() => {
    if (!socket || !event) return;
    const fn = (data) => handlerRef.current?.(data);
    socket.on(event, fn);
    return () => socket.off(event, fn);
  }, [socket, event]);
}
